<button
  #trigger
  #triggerOrigin="cdkOverlayOrigin"
  cdkOverlayOrigin
  class="hx-color-picker__trigger"
  hxButton
  type="button"
  variant="outline"
  [attr.aria-expanded]="isOpen()"
  [attr.aria-label]="ariaLabel()"
  [disabled]="disabled()"
  (click)="togglePicker()"
  (keydown)="onEscapeKey($event)">
  <span class="hx-color-picker__trigger-swatch" [style.background-color]="value()"></span>
</button>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayBackdropClass]="'cdk-overlay-transparent-backdrop'"
  [cdkConnectedOverlayHasBackdrop]="true"
  [cdkConnectedOverlayOpen]="isOpen()"
  [cdkConnectedOverlayOrigin]="triggerOrigin"
  (attach)="onOverlayAttach()"
  (backdropClick)="onBackdropClick()"
  (detach)="closePicker()">
  <div
    #overlayPanel
    class="hx-color-picker__overlay"
    role="dialog"
    [attr.aria-label]="ariaLabel()"
    [attr.aria-modal]="true"
    (keydown)="onEscapeKey($event)">
    <div class="hx-color-picker__container">
      <!-- Tab Navigation -->
      @if (showPalettes() && hasDesignSystemColors()) {
        <div class="hx-color-picker__tabs" role="tablist">
          <button
            class="hx-color-picker__tab"
            role="tab"
            type="button"
            [attr.aria-selected]="activeTab() === 'quick'"
            [attr.tabindex]="activeTab() === 'quick' ? 0 : -1"
            [class.hx-color-picker__tab--active]="activeTab() === 'quick'"
            (click)="setActiveTab('quick')">
            Quick Colors
          </button>
          <button
            class="hx-color-picker__tab"
            role="tab"
            type="button"
            [attr.aria-selected]="activeTab() === 'custom'"
            [attr.tabindex]="activeTab() === 'custom' ? 0 : -1"
            [class.hx-color-picker__tab--active]="activeTab() === 'custom'"
            (click)="setActiveTab('custom')">
            Custom
          </button>
        </div>
      }

      <!-- Quick Colors Tab Panel -->
      @if (showPalettes() && hasDesignSystemColors() && activeTab() === 'quick') {
        <div class="hx-color-picker__tab-panel" role="tabpanel">
          <div class="hx-color-picker__quick-colors">
            <div aria-label="Quick colors from design system" class="hx-color-picker__quick-colors-grid" role="group">
              @for (palette of colorPalettes(); track palette.name; let paletteIndex = $index) {
                @for (color of palette.colors; track color.id; let i = $index) {
                  <button
                    class="hx-color-picker__swatch"
                    type="button"
                    [attr.aria-label]="'Select ' + palette.name.toLowerCase() + ' color ' + color.value"
                    [attr.tabindex]="paletteIndex === 0 && i === 0 ? 0 : -1"
                    [class.hx-color-picker__swatch--light]="isLightColor(color.value)"
                    (click)="applyQuickColor(color.value)">
                    <span class="hx-color-picker__swatch-bg" [style.background-color]="color.value"></span>
                  </button>
                }
              }
            </div>
          </div>
        </div>
      }

      <!-- Custom Color Tab Panel -->
      @if (!showPalettes() || !hasDesignSystemColors() || activeTab() === 'custom') {
        <div class="hx-color-picker__tab-panel" role="tabpanel">
          <div class="hx-color-picker__custom-grid">
            <div class="hx-color-picker__main-controls">
              <!-- Saturation/Value Grid -->
              <div
                #gridContainer
                class="hx-color-picker__grid"
                role="slider"
                tabindex="0"
                [attr.aria-label]="'Color saturation and brightness. Use arrow keys to adjust.'"
                [attr.aria-valuemax]="100"
                [attr.aria-valuemin]="0"
                [attr.aria-valuenow]="s() * 100"
                [style.--grid-hue]="h()"
                (keydown)="onGridKeyDown($event)"
                (mousedown)="onGridMouseDown($event)"
                (touchstart)="onGridTouchStart($event)">
                <div class="hx-color-picker__grid-overlay"></div>
                <div
                  class="hx-color-picker__grid-pointer"
                  role="presentation"
                  [attr.aria-hidden]="true"
                  [style.left.%]="s() * 100"
                  [style.top.%]="(1 - v()) * 100"></div>
              </div>

              <!-- Hue Slider -->
              <div class="hx-color-picker__slider-container">
                <label class="hx-color-picker__slider-label" for="hue-slider">Hue</label>
                <input
                  class="hx-color-picker__hue-slider"
                  id="hue-slider"
                  max="360"
                  min="0"
                  step="1"
                  type="range"
                  [attr.aria-label]="'Hue: ' + Math.round(h()) + ' degrees'"
                  [attr.aria-valuemax]="360"
                  [attr.aria-valuemin]="0"
                  [attr.aria-valuenow]="Math.round(h())"
                  [value]="h()"
                  (input)="onHueChange($event)" />
              </div>

              <!-- Alpha Slider -->
              <div class="hx-color-picker__slider-container">
                <label class="hx-color-picker__slider-label" for="alpha-slider">Opacity</label>
                <input
                  class="hx-color-picker__alpha-slider"
                  id="alpha-slider"
                  max="100"
                  min="0"
                  step="1"
                  type="range"
                  [attr.aria-label]="'Opacity: ' + Math.round(a() * 100) + ' percent'"
                  [attr.aria-valuemax]="100"
                  [attr.aria-valuemin]="0"
                  [attr.aria-valuenow]="Math.round(a() * 100)"
                  [style.--alpha-color]="getOpaqueColor()"
                  [value]="a() * 100"
                  (input)="onAlphaChange($event)" />
              </div>
            </div>

            <!-- Side Panel (Right Column) -->
            <div class="hx-color-picker__side-panel">
              <!-- Format Toggle & Manual Input -->
              <div class="hx-color-picker__inputs">
                <div class="hx-color-picker__format-toggle">
                  <button
                    class="hx-color-picker__format-btn"
                    type="button"
                    [attr.aria-pressed]="currentFormat() === 'hex'"
                    [class.hx-color-picker__format-btn--active]="currentFormat() === 'hex'"
                    (click)="setFormat('hex')">
                    HEX
                  </button>
                  <button
                    class="hx-color-picker__format-btn"
                    type="button"
                    [attr.aria-pressed]="currentFormat() === 'rgba'"
                    [class.hx-color-picker__format-btn--active]="currentFormat() === 'rgba'"
                    (click)="setFormat('rgba')">
                    RGBA
                  </button>
                  <button
                    class="hx-color-picker__format-btn"
                    type="button"
                    [attr.aria-pressed]="currentFormat() === 'hsla'"
                    [class.hx-color-picker__format-btn--active]="currentFormat() === 'hsla'"
                    (click)="setFormat('hsla')">
                    HSLA
                  </button>
                </div>

                <div class="hx-color-picker__input-wrapper">
                  <input
                    class="hx-color-picker__input"
                    placeholder="Enter color"
                    type="text"
                    [attr.aria-invalid]="inputError() ? 'true' : 'false'"
                    [attr.aria-label]="'Color value in ' + currentFormat() + ' format'"
                    [value]="value()"
                    (blur)="validateAndApplyInput()"
                    (input)="onManualInput($event)" />
                  @if (inputError()) {
                    <span class="hx-color-picker__input-error" role="alert">{{ inputError() }}</span>
                  }
                </div>
              </div>

              <!-- EyeDropper Tool -->
              @if (eyeDropperSupported()) {
                <div class="hx-color-picker__eyedropper">
                  <button
                    class="hx-color-picker__eyedropper-btn"
                    type="button"
                    [attr.aria-label]="'Pick color from screen'"
                    (click)="openEyeDropper()">
                    <svg aria-hidden="true" fill="currentColor" height="16" viewBox="0 0 16 16" width="16">
                      <path
                        d="M13.354 2.646a.5.5 0 0 1 0 .708l-1 1a.5.5 0 0 1-.708-.708l1-1a.5.5 0 0 1 .708 0zM11.5 4.5a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5zM9 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5zM7.354 8.354l-5.5 5.5a.5.5 0 0 1-.708 0l-1-1a.5.5 0 0 1 0-.708l5.5-5.5a.5.5 0 0 1 .708 0l1 1a.5.5 0 0 1 0 .708z" />
                      <path
                        d="M11.146 1.646a.5.5 0 0 1 .708 0l2.5 2.5a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-2.5-2.5a.5.5 0 0 1 0-.708l7-7z" />
                    </svg>
                    <span>Pick color</span>
                  </button>
                </div>
              }

              <!-- Brand Presets -->
              @if (uniqueBrandColors().length > 0) {
                <div class="hx-color-picker__presets">
                  <h3 class="hx-color-picker__section-label">Brand</h3>
                  <div class="hx-color-picker__swatch-grid" role="group" [attr.aria-label]="'Brand color presets'">
                    @for (color of uniqueBrandColors(); track color; let i = $index) {
                      <button
                        class="hx-color-picker__swatch"
                        type="button"
                        [attr.aria-label]="'Select brand color ' + color"
                        [attr.tabindex]="i === 0 ? 0 : -1"
                        [style.color]="color"
                        (click)="applyPresetColor(color)">
                        <span class="hx-color-picker__swatch-bg"></span>
                      </button>
                    }
                  </div>
                </div>
              }

              <!-- Recent Colors -->
              @if (recentColors().length > 0) {
                <div class="hx-color-picker__recent">
                  <h3 class="hx-color-picker__section-label">Recent</h3>
                  <div class="hx-color-picker__swatch-grid" role="group" [attr.aria-label]="'Recent colors'">
                    @for (color of recentColors(); track color; let i = $index) {
                      <button
                        class="hx-color-picker__swatch"
                        type="button"
                        [attr.aria-label]="'Select recent color ' + color"
                        [attr.tabindex]="i === 0 ? 0 : -1"
                        [style.color]="color"
                        (click)="applyPresetColor(color)">
                        <span class="hx-color-picker__swatch-bg"></span>
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>
  </div>
</ng-template>
